import React, { useCallback, useMemo, useState } from "react";
import { Form, useFormikContext } from "formik";
import { addDays } from "date-fns";
import { Entity } from "@common/types/entity";
import { DefaultCashBasisAccountCodes } from "@common/constants/domains/accounting/accounts";
import {
    AccountingLedgerBasis,
    BOTH_LEDGER_BASIS,
    LedgerBasisInput,
} from "@common/types/domains/accounting/ledger";
import { LedgerSelect } from "@components/ledger/LedgerSelect";
import { useLedgers } from "@api/ledger.api";
import { useSelectedLedger } from "@hooks/useSelectedLedger";
import {
    fullDateLongFormat,
    toIsoDate,
    toUTCDateOnly,
} from "@common/helpers/date";
import { Drawer } from "@design-system/Drawer";
import { JournalEntrySpreadsheet } from "@components/accounting/journalEntryDrawer/JournalEntrySpreadsheet";
import { useCreateAccountModal } from "@components/accounting/journalEntryDrawer/useAccountCreationCta";
import { useManualJournalAccounts } from "@hooks/useManualJournalAccounts";
import { useLockDate } from "@api/lock.api";
import { useIsDateLocked } from "@hooks/useIsDateLocked";
import { useClasses } from "@api/class.api";
import { JournalEntryDrawerHeader } from "@components/accounting/journalEntryDrawer/JournalEntryDrawerHeader";
import { JournalEntrySidebar } from "@components/accounting/journalEntryDrawer/JournalEntrySidebar";
import { FinancialDocumentUploadProvider } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { FinancialDocument } from "@common/types/financialDocument";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import { Button } from "@design-system/Button/Button";
import { SpreadsheetColumnSettings } from "@components/spreadsheet/SpreadsheetColumnSettings";
import { useSpreadsheetColumnVisibility } from "@components/spreadsheet/useSpreadsheetColumnVisibility";
import { useDefaultLayout, usePanelRef } from "react-resizable-panels";
import { LeftNavClosedIcon, LeftNavOpenedIcon } from "@icons";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { useEntitiesWithAccountingAvailable } from "../useEntitiesWithAccountingAvailable";
import { EntitySelect } from "../../entity/EntitySelect";
import { JournalEntryScopeSelect } from "./JournalEntryScopeSelect";
import { JournalEntryScope } from "./journalEntryScope";
import { JournalEntryConsolidationContext } from "./journalEntryConsolidationContext";
import styles from "./JournalEntryDrawer.module.scss";
import { JournalEntryDrawerFormikConfig } from "./JournalEntryDrawer";
import { JournalEntryDrawerFooter } from "./JournalEntryDrawerFooter";
import { JournalEntryDuplicateRequest } from "./JournalEntryDrawer.lib";
import {
    useIsManualJournalEntry,
    useIsTransactionJournalEntry,
} from "./JournalEntryDrawerHooks";
import {
    JE_COLUMNS,
    JE_SPREADSHEET_COLUMNS_STORAGE_KEY,
} from "./journalEntrySpreadsheetColumns";
import { buildBothLedgersOptions } from "./journalEntryLedgerOptions";
import { buildLedgerOptionOverrides } from "./journalEntryLedgerToggle";
import { HStack } from "designSystem/HStack/HStack";

const DEFAULT_HIDDEN_FIELDS: readonly string[] = ["excludeFrom1099"];

const fields = createFormikFields<JournalEntryDrawerFormikConfig>();

interface Props {
    open: boolean;
    onClose: () => void;
    setFormEntityId: (entityId: number) => void;
    formEntityId: number;
    ledgerSelection: LedgerBasisInput;
    setFormLedgerBasis: (basis: LedgerBasisInput) => void;
    loading: boolean;
    onDelete: () => void;
    onDuplicate: (request: JournalEntryDuplicateRequest) => void;
    allowedAccountIds?: Set<string>;
    scope?: JournalEntryScope;
    entities?: readonly Entity[];
    consolidationContext?: JournalEntryConsolidationContext;
    onScopeChange?: (scope: JournalEntryScope) => void;
    // Transaction-sourced JE only: ledgers (by id) the transaction has a
    // sibling journal entry in, so the user can toggle cash <-> accrual
    // instead of the selector being fully locked post-creation.
    ledgerSwitchTargets?: ReadonlyMap<
        string,
        { referenceNumber: string; ledgerBasis: AccountingLedgerBasis }
    >;
    onSwitchLedger?: (ledgerId: string) => void;
}

export const JournalEntryDrawerContent: React.FC<Props> = ({
    setFormEntityId,
    formEntityId,
    ledgerSelection,
    setFormLedgerBasis,
    loading,
    onDelete,
    onDuplicate,
    open,
    onClose,
    allowedAccountIds,
    scope,
    entities,
    consolidationContext,
    onScopeChange,
    ledgerSwitchTargets,
    onSwitchLedger,
}) => {
    const { defaultLayout, onLayoutChanged } = useDefaultLayout({
        groupId: "journal-entry-resizable-panel-group-v2",
        storage: localStorage,
    });

    const formikContext = useFormikContext<JournalEntryDrawerFormikConfig>();
    const { values, setFieldValue } = formikContext;
    const [isDetailExpanded, setIsDetailExpanded] = useState(true);

    const {
        hiddenFields,
        toggleField,
        reset: resetColumnVisibility,
    } = useSpreadsheetColumnVisibility(
        DEFAULT_HIDDEN_FIELDS,
        JE_SPREADSHEET_COLUMNS_STORAGE_KEY,
    );
    const { isActivated: isClassesActivated } = useClasses();
    const columnSettingsColumns = useMemo(
        () =>
            JE_COLUMNS.filter(
                // Row-number column has an empty label — hide it from the popover.
                // Class column only appears when the feature is active.
                (col) =>
                    col.label !== "" &&
                    (col.field !== "classIds" || isClassesActivated),
            ),
        [isClassesActivated],
    );
    const detailsPanelRef = usePanelRef();

    const isLocked = useIsDateLocked(
        formEntityId,
        values.journalEntryId && values.journalEntry.date
            ? toUTCDateOnly(values.journalEntry.date)
            : undefined,
    );

    const isManualJournal = useIsManualJournalEntry(values);
    const isTransactionJournal = useIsTransactionJournalEntry(values);

    const { accountsById } = useManualJournalAccounts(
        formEntityId,
        values.ledgerBasis,
    );

    const isReversedJournalEntry =
        !!values.journalEntry.reversedManualJournalEntryId;
    const isFieldsEditable =
        isManualJournal && !isLocked && !isReversedJournalEntry;
    const isDuplicable =
        isManualJournal &&
        values.journalEntryId !== undefined &&
        !isReversedJournalEntry;
    const isFooterVisible = isLocked
        ? isDuplicable
        : isManualJournal || isTransactionJournal;
    const isMemoEditable =
        isFieldsEditable ||
        (isTransactionJournal &&
            !isLocked &&
            !values.journalEntry.isCombinedSplitJournalEntry);

    const availableEntities = useEntitiesWithAccountingAvailable();
    const scopeEntities = entities ?? availableEntities;
    const selectedEntity = useMemo(
        () => scopeEntities.find((e) => e.id === values.entityId)!,
        [scopeEntities, values.entityId],
    );
    const { data: ledgers } = useLedgers(selectedEntity?.id);
    const [localSelectedLedgerId, setLocalSelectedLedgerId] = useState<
        string | undefined
    >();
    const selectedLedgerId = useMemo(() => {
        if (
            localSelectedLedgerId &&
            ledgers?.some((ledger) => ledger.id === localSelectedLedgerId)
        ) {
            return localSelectedLedgerId;
        }
        if (
            values.ledgerId &&
            ledgers?.some((ledger) => ledger.id === values.ledgerId)
        ) {
            return values.ledgerId;
        }
        const selectedLedger = ledgers?.find(
            (candidateLedger) => candidateLedger.basis === ledgerSelection,
        );
        return selectedLedger?.id ?? ledgers?.[0]?.id;
    }, [localSelectedLedgerId, values.ledgerId, ledgerSelection, ledgers]);
    const { getLedgerById } = useSelectedLedger(
        selectedEntity?.id,
        selectedLedgerId,
    );

    // "All" is not a ledger id, so it cannot round-trip through selectedLedgerId.
    const isBothLedgersSelected = ledgerSelection === BOTH_LEDGER_BASIS;
    const selectedLedgerValue = isBothLedgersSelected
        ? BOTH_LEDGER_BASIS
        : selectedLedgerId;

    const onEntityChange = useCallback(
        (entity: Entity | undefined) => {
            if (!entity) {
                return;
            }

            setFieldValue("entityId", entity.id);
            setFieldValue("ledgerId", undefined);
            setLocalSelectedLedgerId(undefined);
            setFormEntityId(entity.id);

            // The new entity may not have both primary ledgers, which would make
            // a carried-over "All" selection unsatisfiable on submit.
            if (isBothLedgersSelected) {
                setFormLedgerBasis(AccountingLedgerBasis.cash);
            }

            const defaultCoA = new Set<number>(
                Object.values(DefaultCashBasisAccountCodes).filter(
                    (value): value is number => typeof value === "number",
                ),
            );
            for (const [index, line] of values.journalEntry.lines.entries()) {
                if (line.accountId.length === 0) {
                    continue;
                }
                const { kickOriginalCode } = accountsById[line.accountId];
                const isAccountInDefaultCoA =
                    kickOriginalCode !== null &&
                    defaultCoA.has(kickOriginalCode);
                if (isAccountInDefaultCoA) {
                    continue;
                }
                setFieldValue(`journalEntry.lines.${index}.accountId`, "");
            }
        },
        [
            values.journalEntry.lines,
            setFormEntityId,
            setFieldValue,
            accountsById,
            isBothLedgersSelected,
            setFormLedgerBasis,
        ],
    );

    const handleScopeChange = useCallback(
        (nextScope: JournalEntryScope) => {
            if (!onScopeChange) {
                return;
            }
            switch (nextScope.kind) {
                case "entity": {
                    const nextEntity = scopeEntities.find(
                        (entity) => entity.id === nextScope.entityId,
                    );
                    onEntityChange(nextEntity);
                    onScopeChange(nextScope);
                    return;
                }
                case "consolidationGroup":
                    onScopeChange(nextScope);
                    return;
                default: {
                    const exhaustiveScope: never = nextScope;
                    return exhaustiveScope;
                }
            }
        },
        [onEntityChange, onScopeChange, scopeEntities],
    );

    const isCreateMode = values.journalEntryId === undefined;

    // Posting to both ledgers is only supported on creation; updates are
    // rejected client-side by `upsertJournalEntry`.
    const extraLedgerOptions = useMemo(
        () => (isCreateMode ? buildBothLedgersOptions(ledgers ?? []) : []),
        [isCreateMode, ledgers],
    );

    const onLedgerChange = useCallback(
        (ledgerId: string) => {
            // Transaction-sourced JEs aren't editable in place (isFieldsEditable
            // is false), but the user may still have a sibling entry in the
            // other ledger (e.g. a cash entry plus an accrual override entry
            // for the same transaction). Route the selector to reopen the
            // drawer against that sibling's referenceNumber instead of trying
            // to mutate the current (read-only) form's ledgerId field.
            if (isTransactionJournal) {
                onSwitchLedger?.(ledgerId);
                return;
            }

            if (ledgerId === BOTH_LEDGER_BASIS) {
                setLocalSelectedLedgerId(undefined);
                setFieldValue("ledgerBasis", AccountingLedgerBasis.cash);
                setFieldValue("ledgerId", undefined);
                setFormLedgerBasis(BOTH_LEDGER_BASIS);
                return;
            }

            const selectedLedger = getLedgerById(ledgerId);
            if (!selectedLedger) {
                return;
            }
            setLocalSelectedLedgerId(selectedLedger.id);
            setFieldValue("ledgerBasis", selectedLedger.basis);
            setFieldValue("ledgerId", selectedLedger.id);
            setFormLedgerBasis(selectedLedger.basis);
        },
        [
            isTransactionJournal,
            onSwitchLedger,
            getLedgerById,
            setFieldValue,
            setFormLedgerBasis,
        ],
    );

    // Cash always has a JE for a completed transaction; accrual only when an
    // override account exists for it (see journal-entries-builder.service.ts).
    // Ledgers with no sibling entry stay visible but disabled, with a tooltip
    // explaining why, instead of disappearing or silently no-op-ing on click.
    const ledgerOptionOverrides = useMemo(
        () =>
            isTransactionJournal
                ? buildLedgerOptionOverrides({
                      ledgers: ledgers ?? [],
                      ledgerSwitchTargets,
                  })
                : undefined,
        [isTransactionJournal, ledgerSwitchTargets, ledgers],
    );

    const lockDate = useLockDate(formEntityId);
    // Lock date itself is locked; DatePicker minDate is inclusive, so use the day after.
    const minDate = lockDate ? toIsoDate(addDays(lockDate, 1)) : undefined;

    const handleDuplicate = useCallback(
        () => onDuplicate({ values, lockDate }),
        [onDuplicate, values, lockDate],
    );

    const handleDocumentsUpload = useCallback(
        async (financialDocuments: FinancialDocument[]) => {
            const currentDocuments =
                values.journalEntry.financialDocumentIds ?? [];
            const newDocuments = [
                ...new Set([
                    ...currentDocuments,
                    ...financialDocuments.map((doc) => doc.id),
                ]),
            ];
            setFieldValue("journalEntry.financialDocumentIds", newDocuments);
        },
        [setFieldValue, values],
    );

    const [dismissible, setDismissible] = useState(true);
    const onEnterEditMode = useCallback(() => setDismissible(false), []);
    const onExitEditMode = useCallback(() => setDismissible(true), []);

    const { openCreateAccountModal, createAccountModalElement } =
        useCreateAccountModal({
            formEntityId,
            lines: values.journalEntry.lines,
            memo: values.journalEntry.memo,
            setFieldValue,
        });

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                dismissible={dismissible}
                direction="right"
                noPadding
                size="fullScreen"
                headerAddonLeft={
                    <JournalEntryDrawerHeader>
                        {values.journalEntryId
                            ? "Journal entry"
                            : "New journal entry"}
                    </JournalEntryDrawerHeader>
                }
            >
                <Form className={styles.form}>
                    <div className={styles.content}>
                        <ResizablePanelGroup
                            orientation="horizontal"
                            defaultLayout={defaultLayout}
                            onLayoutChanged={onLayoutChanged}
                        >
                            <ResizablePanel
                                defaultSize="85%"
                                minSize="70%"
                                maxSize="100%"
                                id="journal-entry-left-panel"
                            >
                                <HStack justify="between" padding={12}>
                                    <HStack gap={8}>
                                        <fields.Date
                                            name="journalEntry.date"
                                            label="Date"
                                            labelHidden
                                            variant="panel-input"
                                            className={styles.dateField}
                                            minDate={minDate}
                                            displayFormat={fullDateLongFormat}
                                            disabled={!isFieldsEditable}
                                            buttonProps={{
                                                variant: "primary",
                                                size: "md",
                                            }}
                                        />
                                        <LedgerSelect
                                            selectedEntity={selectedEntity}
                                            selectedLedgerId={
                                                selectedLedgerValue
                                            }
                                            onChange={onLedgerChange}
                                            buttonSize="md"
                                            // Transaction-sourced JEs are never
                                            // editable in place, but the ledger
                                            // selector stays enabled so the user
                                            // can toggle to a sibling entry (see
                                            // onLedgerChange + ledgerOptionOverrides);
                                            // manual JEs keep the original
                                            // create-only gate.
                                            disabled={
                                                isTransactionJournal
                                                    ? false
                                                    : !isFieldsEditable ||
                                                      !isCreateMode
                                            }
                                            extraOptions={extraLedgerOptions}
                                            ledgerOptionOverrides={
                                                ledgerOptionOverrides
                                            }
                                        />
                                        {scope && onScopeChange ? (
                                            <JournalEntryScopeSelect
                                                entities={scopeEntities}
                                                consolidationContext={
                                                    consolidationContext
                                                }
                                                value={scope}
                                                onValueChange={
                                                    handleScopeChange
                                                }
                                                disabled={
                                                    !isFieldsEditable ||
                                                    !isCreateMode
                                                }
                                            />
                                        ) : (
                                            <EntitySelect
                                                entities={availableEntities}
                                                selectedEntity={selectedEntity}
                                                onChange={onEntityChange}
                                                className={styles.entitySelect}
                                                disabled={
                                                    !isFieldsEditable ||
                                                    values.journalEntryId !==
                                                        undefined
                                                }
                                            />
                                        )}
                                    </HStack>
                                    <HStack gap={8}>
                                        <SpreadsheetColumnSettings
                                            columns={columnSettingsColumns}
                                            hiddenFields={hiddenFields}
                                            defaultHiddenFields={
                                                DEFAULT_HIDDEN_FIELDS
                                            }
                                            onToggle={toggleField}
                                            onReset={resetColumnVisibility}
                                        />
                                        <Button
                                            size="md"
                                            variant="primary"
                                            type="button"
                                            iconOnly
                                            aria-label={
                                                isDetailExpanded
                                                    ? "Collapse details"
                                                    : "Expand details"
                                            }
                                            onClick={() =>
                                                detailsPanelRef?.current?.isCollapsed()
                                                    ? detailsPanelRef?.current?.expand()
                                                    : detailsPanelRef?.current?.collapse()
                                            }
                                        >
                                            {isDetailExpanded ? (
                                                <LeftNavOpenedIcon className="icon-color-gray-900 icon-rotate-180" />
                                            ) : (
                                                <LeftNavClosedIcon className="icon-color-gray-900 icon-rotate-180" />
                                            )}
                                        </Button>
                                    </HStack>
                                </HStack>

                                <JournalEntrySpreadsheet
                                    onEnterEditMode={onEnterEditMode}
                                    onExitEditMode={onExitEditMode}
                                    formEntityId={formEntityId}
                                    onOpenCreateAccount={openCreateAccountModal}
                                    allowedAccountIds={allowedAccountIds}
                                    hiddenFields={hiddenFields}
                                />
                            </ResizablePanel>
                            <ResizableHandle />
                            <ResizablePanel
                                defaultSize="15%"
                                collapsible
                                minSize={300}
                                maxSize="30%"
                                id="journal-entry-details-panel"
                                panelRef={detailsPanelRef}
                                onResize={() =>
                                    setIsDetailExpanded(
                                        !(
                                            detailsPanelRef.current?.isCollapsed() ??
                                            false
                                        ),
                                    )
                                }
                                className={styles.sidebarPanel}
                            >
                                <FinancialDocumentUploadProvider
                                    onCompleted={handleDocumentsUpload}
                                >
                                    <JournalEntrySidebar
                                        isEditable={isFieldsEditable}
                                        isMemoEditable={isMemoEditable}
                                    />
                                </FinancialDocumentUploadProvider>
                            </ResizablePanel>
                        </ResizablePanelGroup>
                    </div>

                    <JournalEntryDrawerFooter
                        loading={loading}
                        onDelete={onDelete}
                        onDuplicate={isDuplicable ? handleDuplicate : undefined}
                        isLocked={isLocked}
                        visible={isFooterVisible}
                    />
                </Form>
            </Drawer>
            {createAccountModalElement}
        </>
    );
};
